/**
 * Folding a long run of adjacent citations into a few badges and a "+N".
 *
 * A summary of a long contract cites ten clauses after one sentence, and ten badges in
 * a row wrap onto a line of their own, which reads as noise and pushes the prose apart.
 * The first few stay, because they are the ones the reader is most likely to want; the
 * rest become one `+N` link that `MessageMarkdown` expands in place. Nothing is
 * dropped: every citation stays reachable, and the source list under the reply still
 * has all of them.
 *
 * Works on the Markdown `parseCitations` produces, where a citation is the link
 * `[[n]](#cite-n)`, so it needs no knowledge of any host's ID vocabulary.
 */

/** Runs shorter than this are left alone: three badges read fine inline. */
export const COLLAPSE_AT = 4;
/** How many of a collapsed run stay visible before the "+N". */
export const KEEP_VISIBLE = 2;

/** The fragment a collapsed run's link points at, carrying the hidden numbers. */
export const OVERFLOW_PREFIX = "#cites-";

const RUN = /(?:\[\[\d+\]\]\(#cite-\d+\)){2,}/g;
const ONE = /\[\[(\d+)\]\]\(#cite-\d+\)/g;

export function collapseCitationRuns(content: string): string {
  return content.replace(RUN, (run) => {
    const numbers = Array.from(run.matchAll(ONE), (match) => match[1]);
    if (numbers.length < COLLAPSE_AT) return run;

    const shown = numbers.slice(0, KEEP_VISIBLE);
    const hidden = numbers.slice(KEEP_VISIBLE);
    return (
      shown.map((n) => `[[${n}]](#cite-${n})`).join("") +
      `[+${hidden.length}](${OVERFLOW_PREFIX}${hidden.join(",")})`
    );
  });
}

/** The citation numbers a collapsed run's link carries, or null for any other link. */
export function overflowNumbers(href: string | undefined): number[] | null {
  if (!href?.startsWith(OVERFLOW_PREFIX)) return null;
  const numbers = href
    .slice(OVERFLOW_PREFIX.length)
    .split(",")
    .map((n) => parseInt(n, 10))
    .filter((n) => Number.isInteger(n) && n > 0);
  return numbers.length > 0 ? numbers : null;
}
